import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { EliminationCupMode } from '@scorenco/components';
import { SportType, Types, useTranslation } from '@scorenco/core';

import { T_KEYS } from '../../translations';

import {
  TournamentPhase_GetPhaseDocument,
  type TournamentPhase_GetPhaseQuery,
} from './_internal/TournamentEliminationPhase.query.generated';

type PhaseGame = NonNullable<
  NonNullable<
    NonNullable<
      TournamentPhase_GetPhaseQuery['tournaments_phase_by_pk']
    >['games']
  >[number]
>;

type PhaseGameWithData = PhaseGame & {
  game: Types.Tournaments_Game;
};

const getNumberFromUnknown = (value: unknown) =>
  typeof value === 'number' ? value : undefined;

export const TournamentEliminationPhase = ({
  screen,
}: {
  screen: Types.Clubhouse_Screen;
}) => {
  const { t } = useTranslation();
  const phaseId = screen.phase_id;

  const getPhase = useQuery(TournamentPhase_GetPhaseDocument, {
    variables: {
      phaseId: phaseId ?? 0,
    },
    skip: !phaseId,
    context: {
      role: 'club',
    },
    fetchPolicy: 'cache-and-network',
  });

  const phase = getPhase?.data?.tournaments_phase_by_pk;
  const rule = phase?.rule;
  const ruleType = getNumberFromUnknown(rule?.rule_type);
  const rulePeriodNb = getNumberFromUnknown(rule?.period_nb);
  const rulePeriodDuration = getNumberFromUnknown(rule?.period_duration) ?? 0;

  const eventToDisplay =
    ruleType === 3 || (ruleType === 6 && phase?.rank === 1) ? 4 : 6;

  const phaseGames = (phase?.games ?? []).filter(
    (item): item is PhaseGameWithData =>
      item?.game?.__typename === 'tournaments_game'
  );
  const gameOffset = (phaseGames.length - eventToDisplay - 4) * 15;
  const shouldAnimate = phaseGames.length - eventToDisplay > 4;
  const isVolley =
    getPhase.data?.tournaments_tournament?.[0]?.sport_id === SportType.Volley;

  return (
    <Box>
      <Typography
        variant="h1"
        sx={{
          m: 0,
          textAlign: 'center',
          marginTop: '3rem',
        }}
      >
        {getPhase?.data?.tournaments_tournament?.[0]?.name}
      </Typography>
      <Typography
        variant="h2"
        sx={{
          m: 0,
          textAlign: 'center',
          // marginTop: '3rem',
        }}
      >
        {getPhase?.data?.tournaments_tournament?.[0]?.challenges?.[0]?.name}
      </Typography>
      <Typography
        variant="h3"
        css={css`
          margin: 0.75rem 1rem 1.8rem;
          font-size: 1.5rem;
          text-align: center;
        `}
      >
        {phase?.name}

        {isVolley && rule ? (
          <>
            <span
              css={css`
                margin-left: 1rem;
                font-weight: 500;
              `}
            >
              (Matchs de {rulePeriodNb ?? 0} sets gagnants)
            </span>
          </>
        ) : (
          rule &&
          rulePeriodDuration > 0 && (
            <span
              css={css`
                margin-left: 1rem;
                font-weight: 500;
              `}
            >
              (
              {rulePeriodNb === 1
                ? `Matchs de ${rulePeriodDuration} minutes`
                : `Mi-temps de ${rulePeriodDuration} minutes`}
              )
            </span>
          )
        )}
      </Typography>
      <Box
        sx={{
          width: '90%',
          m: 'auto',
        }}
      >
        <Typography
          variant="h3"
          sx={{
            textTransform: 'uppercase',
            textAlign: 'center',
            borderBottom: '3px solid',
            borderBottomColor: (theme) => theme.vars.palette.primary.main,
            p: '0 1.5rem 1rem 1.5rem',
            mb: '1rem',
            textAlignLast: 'left',
            width: 'fit-content',
          }}
        >
          {t(T_KEYS.TOURNAMENTS_ELIMINATION_PHASE_NAME)}
        </Typography>
      </Box>
      <Paper
        sx={{
          width: '90%',
          overflow: 'hidden',
          m: 'auto',
          position: 'relative',
        }}
      >
        <Stack
          direction="row"
          spacing={4}
          sx={{
            width: '100%',
            px: 4,
            mt: 4,
          }}
        >
          <Box sx={{ flex: 4 }}>
            <Box
              css={css`
                @keyframes scrollEventCup {
                  0% {
                    transform: translateY(0);
                  }
                  15% {
                    transform: translateY(0);
                  }
                  85% {
                    transform: translateY(-${gameOffset}rem);
                  }
                  100% {
                    transform: translateY(-${gameOffset}rem);
                  }
                }
                animation: ${shouldAnimate
                  ? `scrollEventCup ${screen?.duration}s  linear infinite`
                  : ''};
              `}
            >
              <EliminationCupMode games={phaseGames} />
            </Box>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};
